/**
 * The detect_cluster behavior in the browser — a run over a scope, or a sweep
 * of some items against the rest of the workspace.
 *
 * SPEC-custom-agent-template.md §4: one runner, two hosts. This is the browser
 * host (Run now over a scope; the sweep after a session-results import); the
 * server host is supabase/functions/ambient/lib/clusterServer.ts (events,
 * schedules, tasks). Both build the prompt, parse the reply, merge groups and
 * plan sweeps with the shared core (supabase/functions/_shared/clusterCore.ts),
 * and both write through propose_item_cluster — the same row either way.
 *
 * Every run: the agent must be on and the plan must include AI; each model
 * call counts against the agent's monthly cap; nothing is written the
 * definition does not declare; every proposal writes a decision record.
 */

import { getSupabaseClient } from './supabaseClient';
import { getAgentSettings, type AgentSettings } from './agentSettingsService';
import { TWINS, TWINS_AGENT_TYPE, agentReportHref, assertAgentMayProduce } from './ai/agentDefinitions';
import {
  applyClusterConfig,
  clusterKey,
  planSweepBatches,
  resolveClusterConfigFrom,
  touchesTargets,
  type ClusterBehaviorConfig,
  type ClusterItemInput,
  type ClusterResult,
} from '../../supabase/functions/_shared/clusterCore.ts';
import { runTwinsAgent, TWINS_BATCH_SIZE } from './llmTwinsService';
import { createTwinsCluster } from './twinsAgentService';
import { countAgentModelCall, enqueueAgentDelivery } from './agentRuntimeService';
import { isWorkspaceEntitledToAi } from './ai/aiAssistSettings';
import { fetchItemsForScope } from './agentScopeItems';
import type { BatchStats, RunScope } from './agentRunService';
import { currentAgentRunId } from './agentRunService';

export interface ClusterAgent {
  agentType: string;
  name: string;
}

export const TWINS_IDENTITY: ClusterAgent = { agentType: TWINS_AGENT_TYPE, name: TWINS.name };

export const CLUSTER_STACK_VERSION = 'detect_cluster/2';

export function resolveClusterConfig(settings: AgentSettings | null): ClusterBehaviorConfig {
  return resolveClusterConfigFrom(settings?.behaviorConfig);
}

async function record(agent: ClusterAgent, workspaceId: string, r: {
  itemId: string; outputKind: 'proposal:cluster' | 'error'; rationale: string; outputId?: string | null;
  proposedValue?: unknown; runId?: string | null; runByUserId?: string | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('record_agent_decision', {
    p_workspace_id: workspaceId,
    p_agent_type: agent.agentType,
    p_object_id: r.itemId,
    p_output_kind: r.outputKind,
    p_rationale: r.rationale,
    p_object_type: 'item',
    p_field: 'cluster',
    p_output_id: r.outputId ?? null,
    p_proposed_value: r.proposedValue ?? null,
    p_confidence: null,
    p_evidence: [],
    p_stack_version: CLUSTER_STACK_VERSION,
    p_model: null,
    p_run_id: r.runId ?? null,
    p_sponsor_user_id: null,
    p_run_by_user_id: r.runByUserId ?? null,
    p_task_id: null,
  });
  if (error) console.warn('[clusters] decision record not written:', error.message);
}

interface BatchContext {
  agent: ClusterAgent;
  workspaceId: string;
  settings: AgentSettings;
  config: ClusterBehaviorConfig;
  runId: string | null;
  runByUserId: string | null;
  /** Proposals count only when they name one of these (a sweep); null = any. */
  targets: Set<string> | null;
  rationaleSuffix?: string;
  seen: Set<string>;
}

/** One model call over one batch, then each new group proposed. */
async function runOneBatch(ctx: BatchContext, batch: ClusterItemInput[], stats: BatchStats, proposedItems: Set<string>): Promise<boolean> {
  const meter = await countAgentModelCall(ctx.workspaceId, ctx.agent.agentType);
  if (!meter.allowed) {
    await record(ctx.agent, ctx.workspaceId, { itemId: batch[0].id, outputKind: 'error', rationale: meter.error ?? `${ctx.agent.name} has used its model calls for this month.`, runId: ctx.runId, runByUserId: ctx.runByUserId });
    stats.errors += 1;
    return false;
  }
  let clusters: ClusterResult[];
  try {
    clusters = await runTwinsAgent({
      items: batch,
      model: ctx.settings.model,
      systemPrompt: ctx.settings.systemPrompt ?? null,
      modelParams: ctx.settings.modelParams ?? null,
      referenceDocs: ({ reference_docs: true, ...(ctx.settings.evidenceSources ?? {}) }).reference_docs ? ctx.settings.referenceDocs ?? [] : [],
      timeoutMs: (ctx.settings.limits as { timeoutMs?: number } | undefined)?.timeoutMs,
    });
  } catch (err) {
    await record(ctx.agent, ctx.workspaceId, { itemId: batch[0].id, outputKind: 'error', rationale: err instanceof Error ? err.message : 'The model call failed.', runId: ctx.runId, runByUserId: ctx.runByUserId });
    stats.errors += 1;
    stats.processed += batch.length;
    return true;
  }
  stats.processed += batch.length;
  for (const cluster of applyClusterConfig(clusters, ctx.config)) {
    if (ctx.targets && !touchesTargets(cluster, ctx.targets)) continue;
    const key = clusterKey(cluster.item_ids);
    if (ctx.seen.has(key)) continue;
    ctx.seen.add(key);
    assertAgentMayProduce(TWINS, 'proposal:cluster');
    const rationale = ctx.rationaleSuffix ? `${cluster.rationale} ${ctx.rationaleSuffix}` : cluster.rationale;
    const { data, existing, error } = await createTwinsCluster(ctx.workspaceId, { clusterType: cluster.cluster_type, itemIds: cluster.item_ids, rationale }, ctx.agent.agentType);
    if (error || !data) { stats.errors += 1; continue; }
    if (existing) { stats.skipped += 1; continue; }
    await record(ctx.agent, ctx.workspaceId, { itemId: cluster.item_ids[0], outputKind: 'proposal:cluster', outputId: data.id, rationale, proposedValue: { cluster_type: cluster.cluster_type, item_ids: cluster.item_ids }, runId: ctx.runId, runByUserId: ctx.runByUserId });
    stats.proposed += 1;
    for (const id of cluster.item_ids) proposedItems.add(id);
  }
  return true;
}

async function announce(agent: ClusterAgent, workspaceId: string, stats: BatchStats, proposedItems: Set<string>, runId: string | null): Promise<void> {
  if (!stats.proposed) return;
  await enqueueAgentDelivery({
    workspaceId,
    agentType: agent.agentType,
    title: `${agent.name} found ${stats.proposed} possible duplicate group${stats.proposed === 1 ? '' : 's'}`,
    body: `${stats.proposed} group${stats.proposed === 1 ? '' : 's'} are waiting for review.`,
    link: agentReportHref(agent.agentType),
    itemIds: [...proposedItems],
    runId,
  });
}


async function ready(workspaceId: string, agent: ClusterAgent): Promise<AgentSettings | null> {
  if (!workspaceId || !isWorkspaceEntitledToAi(workspaceId)) return null;
  const { data: settings } = await getAgentSettings(workspaceId, agent.agentType);
  return settings?.enabled ? settings : null;
}

/**
 * Run over a scope (matches AgentBatchRunner). Stats: processed = items sent
 * to the model; proposed = new groups; skipped = groups already open;
 * errors = failed calls or saves. Progress is counted in batches.
 */
export async function runClusterBatch(
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
  agent: ClusterAgent = TWINS_IDENTITY,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  const settings = await ready(workspaceId, agent);
  if (!settings) return stats;
  const cap = Number((settings.limits as { perRunObjectCap?: number } | undefined)?.perRunObjectCap ?? TWINS.limits.perRunObjectCap);
  const { items, error } = await fetchItemsForScope(workspaceId, scope, { agentType: agent.agentType, exitStatuses: ['active'], limit: cap });
  if (error) { stats.errors += 1; return stats; }
  stats.itemsRead = items.length;
  if (items.length < 2) return stats;

  const ctx: BatchContext = {
    agent, workspaceId, settings, config: resolveClusterConfig(settings),
    runId: await currentAgentRunId(workspaceId, agent.agentType), runByUserId: runByUserId ?? null, targets: null, seen: new Set(),
  };
  const failStop = (settings.limits as { failurePosture?: string } | undefined)?.failurePosture === 'fail_run';
  const proposedItems = new Set<string>();
  // Progress is counted in items, as every other runner counts it.
  const total = items.length;
  onProgress(0, total);
  for (let offset = 0; offset < items.length; offset += TWINS_BATCH_SIZE) {
    if (signal.aborted) break;
    const before = stats.errors;
    const goOn = await runOneBatch(ctx, items.slice(offset, offset + TWINS_BATCH_SIZE), stats, proposedItems);
    onProgress(Math.min(offset + TWINS_BATCH_SIZE, total), total);
    if (!goOn || (failStop && stats.errors > before)) break;
  }
  await announce(agent, workspaceId, stats, proposedItems, ctx.runId);
  return stats;
}

/**
 * Check some items (just imported, or just created) against the rest of the
 * workspace's active items. A group counts only when it names one of them.
 */
export async function sweepItemsForClusters(params: {
  workspaceId: string;
  targets: ClusterItemInput[];
  others: ClusterItemInput[];
  agent?: ClusterAgent;
  rationaleSuffix?: string;
  runByUserId?: string | null;
}): Promise<{ ran: boolean; reason?: 'agent-off' | 'nothing-to-compare'; stats: BatchStats }> {
  const agent = params.agent ?? TWINS_IDENTITY;
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  const settings = await ready(params.workspaceId, agent);
  if (!settings) return { ran: false, reason: 'agent-off', stats };
  const batches = planSweepBatches(params.targets, params.others);
  if (!batches.length) return { ran: false, reason: 'nothing-to-compare', stats };
  const ctx: BatchContext = {
    agent, workspaceId: params.workspaceId, settings, config: resolveClusterConfig(settings), runId: null,
    runByUserId: params.runByUserId ?? null, targets: new Set(params.targets.map((t) => t.id)),
    rationaleSuffix: params.rationaleSuffix, seen: new Set(),
  };
  const proposedItems = new Set<string>();
  for (const batch of batches) {
    if (!(await runOneBatch(ctx, batch, stats, proposedItems))) break;
  }
  await announce(agent, params.workspaceId, stats, proposedItems, null);
  return { ran: true, stats };
}
